import { Flex, chakra } from '@chakra-ui/react';
import React from 'react';

import { route } from 'nextjs-routes';

import config from 'configs/app';
import { useI18n } from 'lib/contexts/i18n';
import { getBrandName } from 'lib/i18n/brandName';
import { useColorModeValue } from 'toolkit/chakra/color-mode';
import { Image } from 'toolkit/chakra/image';

const DEFAULT_LOGO_SRC = '/assets/favicon/logo-icon.png';
const INVERT_FILTER = 'brightness(0) invert(1)';
const LOGO_SIZE_EXPANDED = '44px';
const LOGO_SIZE_COLLAPSED = '40px';

interface Props {
  isCollapsed?: boolean;
  onClick?: (event: React.SyntheticEvent) => void;
  className?: string;
}

const NetworkLogo = ({ isCollapsed, onClick, className }: Props) => {
  const { locale } = useI18n();
  const brandName = getBrandName(locale);

  const configuredLogo = useColorModeValue(
    config.UI.navigation.logo.default,
    config.UI.navigation.logo.dark || config.UI.navigation.logo.default,
  );
  const configuredIcon = useColorModeValue(
    config.UI.navigation.icon.default,
    config.UI.navigation.icon.dark || config.UI.navigation.icon.default,
  );

  const logoSrc = configuredLogo || DEFAULT_LOGO_SRC;
  const iconSrc = configuredIcon || configuredLogo || DEFAULT_LOGO_SRC;
  const showBrandText = isCollapsed !== true;

  return (
    <chakra.a
      className={ className }
      href={ route({ pathname: '/' }) }
      width={{ base: '100%', lg: isCollapsed === false ? '100%' : LOGO_SIZE_COLLAPSED, xl: isCollapsed ? LOGO_SIZE_COLLAPSED : '100%' }}
      minW={{ lg: isCollapsed === false ? 'auto' : LOGO_SIZE_COLLAPSED, xl: isCollapsed ? LOGO_SIZE_COLLAPSED : 'auto' }}
      height={{ base: 'auto', lg: isCollapsed === false ? 'auto' : LOGO_SIZE_COLLAPSED, xl: isCollapsed ? LOGO_SIZE_COLLAPSED : 'auto' }}
      minH={ LOGO_SIZE_COLLAPSED }
      display="inline-flex"
      alignItems="center"
      overflow="hidden"
      onClick={ onClick }
      flexShrink={ 0 }
      aria-label="Link to main page"
    >
      <Flex alignItems="center" columnGap={ 3 } overflow="hidden" w="100%">
        <Image
          boxSize={ LOGO_SIZE_EXPANDED }
          flexShrink={ 0 }
          src={ logoSrc }
          alt={ `${ config.chain.name } network logo` }
          fallback={
            <Image
              boxSize={ LOGO_SIZE_EXPANDED }
              src={ DEFAULT_LOGO_SRC }
              alt={ `${ config.chain.name } network logo` }
              objectFit="contain"
            />
          }
          display={{ base: 'block', lg: isCollapsed === false ? 'block' : 'none', xl: isCollapsed ? 'none' : 'block' }}
          filter={{ _dark: configuredLogo && !config.UI.navigation.logo.dark ? INVERT_FILTER : undefined }}
          objectFit="contain"
          objectPosition="left"
        />
        <Image
          boxSize={ LOGO_SIZE_COLLAPSED }
          flexShrink={ 0 }
          src={ iconSrc }
          alt={ `${ config.chain.name } network icon` }
          fallback={
            <Image
              boxSize={ LOGO_SIZE_COLLAPSED }
              src={ DEFAULT_LOGO_SRC }
              alt={ `${ config.chain.name } network icon` }
              objectFit="contain"
            />
          }
          display={{ base: 'none', lg: isCollapsed === false ? 'none' : 'block', xl: isCollapsed ? 'block' : 'none' }}
          filter={{ _dark: configuredIcon && !config.UI.navigation.icon.dark ? INVERT_FILTER : undefined }}
          objectFit="contain"
          objectPosition="center"
        />
        { showBrandText && (
          <chakra.span
            fontWeight={ 600 }
            fontSize="lg"
            lineHeight="short"
            whiteSpace="nowrap"
            minW={ 0 }
            display={{ base: 'block', lg: isCollapsed === false ? 'block' : 'none', xl: isCollapsed ? 'none' : 'block' }}
          >
            { brandName }
          </chakra.span>
        ) }
      </Flex>
    </chakra.a>
  );
};

export default React.memo(chakra(NetworkLogo));
